import { createContext, use, useMemo, type ReactNode } from 'react';
import {
  useDismiss,
  useFloating,
  useInteractions,
  type UseInteractionsReturn,
  useRole,
} from '@floating-ui/react';
import { type DrawerAction, type DrawerOptions } from './types';

type UseDrawerReturn = {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  disableClose?: boolean;
  closeOnClickOutside?: boolean;
  secondaryAction?: DrawerAction;
  primaryAction?: DrawerAction;
  width?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
} & ReturnType<typeof useFloating> &
  UseInteractionsReturn;

export const useDrawer = ({
  open,
  onOpenChange,
  onClose,
  secondaryAction,
  primaryAction,
  width,
  title,
  subtitle,
  disableClose,
  closeOnClickOutside,
  closeOnEscape = true,
}: DrawerOptions): UseDrawerReturn => {
  const data = useFloating({
    open,
    onOpenChange,
  });

  const context = data.context;

  const dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown',
    // Outside-click closing is handled exclusively by the overlay's own `onClick` handler (see
    // `OverlayWrapper` in `index.tsx`), since that path is resilient to an externally supplied
    // `onOpenChange` (e.g. Storybook's actions addon auto-mocking `onOpenChange`). Letting
    // `useDismiss` also react to outside presses here would call `onOpenChange`/`onClose` a
    // second time for the same click.
    outsidePress: false,
    escapeKey: closeOnEscape,
  });

  const role = useRole(context);

  const interactions = useInteractions([dismiss, role]);

  const val = useMemo(() => {
    const value: UseDrawerReturn = {
      open,
      ...interactions,
      ...data,
      onClose,
      onOpenChange,
      disableClose,
      closeOnClickOutside,
      secondaryAction,
      primaryAction,
      width,
      title,
      subtitle,
    };

    return value;
  }, [
    open,
    interactions,
    data,
    onClose,
    onOpenChange,
    disableClose,
    closeOnClickOutside,
    secondaryAction,
    primaryAction,
    width,
    title,
    subtitle,
  ]);

  return val;
};

type ContextType = ReturnType<typeof useDrawer> | null;

export const DrawerContext = createContext<ContextType>(null);

export const useDrawerContext = () => {
  const context = use(DrawerContext);

  if (context === null) {
    throw new Error('Drawer components must be wrapped in <Drawer />');
  }

  return context;
};
